import type { ComponentType } from "react";
import { PresentationEffect } from "@/components/blocks/PresentationEffect";
import type { ContentBlock } from "@/types/page";
import type { SiteTemplateKey } from "@/lib/siteTemplates";
import { HeroBlock } from "@/components/blocks/HeroBlock";
import { TextBlock } from "@/components/blocks/TextBlock";
import { CtaBlock } from "@/components/blocks/CtaBlock";
import { ImageBlock } from "@/components/blocks/ImageBlock";
import { VideoBlock } from "@/components/blocks/VideoBlock";
import { EmbedBlock } from "@/components/blocks/EmbedBlock";
import { SpacerBlock } from "@/components/blocks/SpacerBlock";
import { DividerBlock } from "@/components/blocks/DividerBlock";
import { QuoteBlock } from "@/components/blocks/QuoteBlock";
import { ButtonBlock } from "@/components/blocks/ButtonBlock";
import { ColumnsBlock } from "@/components/blocks/ColumnsBlock";
import { GroupBlock } from "@/components/blocks/GroupBlock";
import { PricingBlock } from "@/components/blocks/PricingBlock";
import { TestimonialsBlock } from "@/components/blocks/TestimonialsBlock";
import { MapBlock } from "@/components/blocks/MapBlock";
import { FormBlock } from "@/components/blocks/FormBlock";
import { FeatureGridBlock } from "@/components/blocks/FeatureGridBlock";
import { LogoStripBlock } from "@/components/blocks/LogoStripBlock";
import { StatsBandBlock } from "@/components/blocks/StatsBandBlock";
import { ImageTextSplitBlock } from "@/components/blocks/ImageTextSplitBlock";
import { GalleryCarouselBlock } from "@/components/blocks/GalleryCarouselBlock";
import { TeamPreviewBlock } from "@/components/blocks/TeamPreviewBlock";
import { CtaBannerBlock } from "@/components/blocks/CtaBannerBlock";
import { SocialLinksBlock } from "@/components/blocks/SocialLinksBlock";
import { MusicReleasesBlock } from "@/components/blocks/MusicReleasesBlock";
import { AccordionBlock } from "@/components/blocks/AccordionBlock";
import { ServicesListBlock } from "@/components/blocks/ServicesListBlock";
import {
  ProductShowcaseBlock,
  ClientExamplesBlock,
  BeforeAfterBlock,
  ProblemSolutionResultBlock,
  UseCasesBlock,
  CtaOfferBlock,
} from "@/components/blocks/ProofBlocks";

type BlockComponentProps = {
  content: Record<string, unknown>;
  template?: SiteTemplateKey;
  headingLevel?: 1 | 2;
};

const BLOCKS: Record<string, ComponentType<BlockComponentProps>> = {
  hero: HeroBlock,
  text: TextBlock,
  cta: CtaBlock,
  image: ImageBlock,
  video: VideoBlock,
  embed: EmbedBlock,
  spacer: SpacerBlock,
  divider: DividerBlock,
  quote: QuoteBlock,
  button: ButtonBlock,
  columns: ColumnsBlock,
  group: GroupBlock,
  icon_text: TextBlock,
  testimonials: TestimonialsBlock,
  pricing: PricingBlock,
  accordion: AccordionBlock,
  map: MapBlock,
  form: FormBlock,
  table: TextBlock,
  feature_grid: FeatureGridBlock,
  logo_strip: LogoStripBlock,
  stats_band: StatsBandBlock,
  image_text_split: ImageTextSplitBlock,
  gallery_carousel: GalleryCarouselBlock,
  team_preview: TeamPreviewBlock,
  cta_banner: CtaBannerBlock,
  social_links: SocialLinksBlock,
  music_releases: MusicReleasesBlock,
  services_list: ServicesListBlock,
  product_showcase: ProductShowcaseBlock,
  client_examples: ClientExamplesBlock,
  before_after: BeforeAfterBlock,
  problem_solution_result: ProblemSolutionResultBlock,
  use_cases: UseCasesBlock,
  cta_offer: CtaOfferBlock,
};

export function BlockRenderer({
  blocks,
  template = "classic",
  heroStartsAtH2 = false,
}: {
  blocks: ContentBlock[];
  template?: SiteTemplateKey;
  heroStartsAtH2?: boolean;
}) {
  let usedHeroH1 = heroStartsAtH2;
  return (
    <>
      {blocks
        .filter((b) => b.published)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((block) => {
          const Component = BLOCKS[block.blockType] ?? TextBlock;
          const headingLevel = block.blockType === "hero" && !usedHeroH1 ? 1 : 2;
          if (block.blockType === "hero") {
            usedHeroH1 = true;
          }
          return (
            <PresentationEffect key={block.id} effect={block.content.presentationEffect}>
            <Component
              content={block.content}
              template={template}
              headingLevel={headingLevel}
            />
            </PresentationEffect>
          );
        })}
    </>
  );
}
